import {append} from '@packages/utilities/array';
import {constant} from '@packages/utilities/fp/utils';
import {matchExpression, Variable} from '@packages/systems/wfdl';
import {getName, getPluginName} from '@packages/systems/wfdl-Qualified';
import template from '@babel/template';
import generate from '@babel/generator';
import * as t from '@babel/types';
import {typeToAST} from './TypeToSource';

import {type Expression} from '@packages/systems/wfdl-ast';

const DataTemplate = template.expression(`
  EData(%%path%%)
`);

const VariableTemplate = template.expression(`
  EVariable(
    Variable.unqualified(identifier(%%value%%))
  )
`);

const VariableTemplateQualified = template.expression(
  `Expressions.qualifiedVariable(%%plugin%%, %%value%%)`
);

const FunctionTemplate = template.expression(`
  EFunction({
    param: %%param%%,
    body: %%body%%
  })
`);

const CallTemplate = template.expression(`
  ECall({
    fun: %%fun%%,
    arg: %%arg%%
  })
`);

const HoleTemplate = template.expression(`EHole()`);
const NumberTemplate = template.expression(`ENumber(%%value%%)`);
const TextTemplate = template.expression(`EText(%%value%%)`);
const EnumTemplate = template.expression(`EEnum(%%value%%)`);
const BooleanTemplate = template.expression(`EBoolean(%%value%%)`);
const LiteralTemplate = template.expression(`ELiteral({
  name: qualify(%%pluginName%%, %%typeName%%),
  value: %%value%%
})`);
const ListTemplate = template.expression(`EList(%%value%%)`);
const RecordTemplate = template.expression(`ERecord(%%value%%)`);
const SelectTemplate = template.expression(`ESelect({
  from: %%from%%,
  prop: %%prop%%
})`);
const UnionTemplate = template.expression(`EUnion({
  tag: %%tag%%,
  value: %%value%%
})`);
const CaseTemplate = template.expression(`
  ECase({
    value: %%value%%,
    cases: %%cases%%
  })
`);

const ElementTemplate = template.expression(`
  EElement({
    id: %%id%%,
    type: qualify(Plugins.name(%%plugin%%), Component.name(%%type%%)),
    data: %%data%%
  })
`);
const TypedTemplate = template.expression(`
  ETyped({
    type: %%type%%,
    expr: %%expr%%
  })
`);
const ExtendTemplate = template.expression(`
  EExtend({
    record: %%record%%,
    prop: %%prop%%,
    value: %%value%%
  })
`);

const DistinctTemplate = template.expression(`
  EDistinct({
    name: qualify(%%pluginName%%, %%typeName%%),
    value: %%value%%
  })
`);

const PluginNameTemplate = template.expression(`Plugins.name(NAME)`);
const TypeNameTemplate = template.expression(`typeName(NAME)`);
const JSONTemplate = template.expression(
  `/* Not enough information to effectively generate source code. */ JSON.parse(%%value%%)`
);

const selectPathDataTemplate = matchExpression({
  Variable: (val) => (path: Array<any | string>) =>
    val === 'data' && path.length
      ? DataTemplate({
          path: t.arrayExpression(path.map((item) => t.stringLiteral(item))),
        })
      : undefined,
  _: constant(constant(undefined)),
});

const toSelectPath: (expr: Expression) => {from: Expression; path: string[]} =
  matchExpression({
    Select: ({from, prop}) => {
      const inner = toSelectPath(from);
      return {from: inner.from, path: append(inner.path, prop)};
    },
    _: (expr) => ({from: expr, path: []}),
  });

const toASTHandler: (expr: Expression) => t.Expression = matchExpression({
  Extend: (val) =>
    ExtendTemplate({
      prop: t.stringLiteral(val.prop),
      record: toAST(val.record),
      value: toAST(val.value),
    }),
  Variable: Variable.match({
    unqualified: (v) => VariableTemplate({value: t.stringLiteral(v)}),
    qualified: (v) =>
      VariableTemplateQualified({
        value: t.stringLiteral(getName(v)),
        plugin: t.stringLiteral(getPluginName(v)),
      }),
  }),
  Function: (val) =>
    FunctionTemplate({
      param: t.stringLiteral(val.param),
      body: toAST(val.body),
    }),
  Call: (val) =>
    CallTemplate({
      fun: toAST(val.fun),
      arg: toAST(val.arg),
    }),
  Hole: constant(HoleTemplate()),
  Number: (val) => NumberTemplate({value: t.numericLiteral(val)}),
  Text: (val) => TextTemplate({value: t.stringLiteral(val)}),
  List: (val) =>
    ListTemplate({
      value: t.arrayExpression(val.map(toAST)),
    }),
  Record: (val) =>
    RecordTemplate({
      value: t.objectExpression(
        Object.keys(val).map((key) =>
          // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
          t.objectProperty(t.stringLiteral(key), toAST(val[key]))
        )
      ),
    }),
  Select: (val) =>
    SelectTemplate({
      from: toAST(val.from),
      prop: t.stringLiteral(val.prop),
    }),
  Union: (val) =>
    UnionTemplate({
      tag: t.stringLiteral(val.tag),
      value: toAST(val.value),
    }),
  Case: (val) =>
    CaseTemplate({
      value: toAST(val.value),
      cases: t.objectExpression(
        Object.keys(val.cases).map((key) =>
          // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
          t.objectProperty(t.stringLiteral(key), toAST(val.cases[key]))
        )
      ),
    }),
  Element: (val) => {
    return ElementTemplate({
      id: t.stringLiteral(val.id),
      plugin: t.stringLiteral(getPluginName(val.type)),
      type: t.stringLiteral(getName(val.type)),
      data: toAST(val.data),
    });
  },
  Boolean: (val) => BooleanTemplate({value: t.booleanLiteral(val)}),
  Enum: (val) => EnumTemplate({value: t.stringLiteral(val)}),
  Literal: (val) =>
    LiteralTemplate({
      pluginName: PluginNameTemplate({
        NAME: t.stringLiteral(getPluginName(val.name)),
      }),
      typeName: TypeNameTemplate({
        NAME: t.stringLiteral(getName(val.name)),
      }),
      value: JSONTemplate({
        value: t.stringLiteral(JSON.stringify(val.value)),
      }),
    }),
  Typed: (val) =>
    TypedTemplate({
      type: typeToAST(val.type),
      expr: toAST(val.expr),
    }),
  Distinct: (val) => {
    return DistinctTemplate({
      pluginName: PluginNameTemplate({
        NAME: t.stringLiteral(getPluginName(val.name)),
      }),
      typeName: TypeNameTemplate({
        NAME: t.stringLiteral(getName(val.name)),
      }),
      value: toAST(val.value),
    });
  },
});

export function toAST(expression: Expression): t.Expression {
  const {from, path} = toSelectPath(expression);

  const fromDataTemplate = selectPathDataTemplate(from)(path);
  if (fromDataTemplate) return fromDataTemplate;

  return toASTHandler(expression);
}

export function toSource(expression: Expression): string {
  return generate(toAST(expression)).code;
}
